import React, { useState, useEffect, useMemo } from 'react';
import { GAPS_FILTER_COLUMNS } from 'constants/reports';
import { GapFilterData } from 'store/ducks/Reporting/types';
import { useFilterOptions } from 'utils/hooks/useFilterOptions';
import { useSelector } from 'react-redux';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import ApiService from 'utils/ApiCalls';
import CustomTable from 'components/Tables/CustomTable';
import * as Styled from './GapsFilter.styled';
import { useModuleOptions } from 'utils/hooks/useModule';
import { useReportExport } from 'utils/hooks/useReportExport';

const GapsFilter: React.FC = () => {
  const { loading } = useFilterOptions();
  const [data, setData] = useState<GapFilterData[]>([]);
  const { registerReport, clearReport } = useReportExport();

  const columns = useMemo(() => GAPS_FILTER_COLUMNS, []);

  const { orgContext, lang } = useSelector((state: RootState) => ({
    orgContext: state.global.hierarchicalLevelsScope,
    lang: state.global.lang
  }));

  const filterParams = Helpers.toApiFilters(orgContext);
  const normalizedFilters = Helpers.normalizeFilters(
    Helpers.toApiFilters(orgContext)
  );
  const industry = orgContext?.industryType || [];

  const { moduleOptionsState, fetchModules } = useModuleOptions(industry);

  const handleDisplay = async () => {
    try {
      if (!orgContext || Object.keys(orgContext).length === 0) return;

      const hasFilters = Object.keys(filterParams).length > 0;
      if (!hasFilters) return;

      // Get modules first
      const modules =
        moduleOptionsState.length > 0
          ? moduleOptionsState
          : await fetchModules();

      const selectedModule = modules.find(
        (module) => Number(module.value) === Number(normalizedFilters.module)
      );

      const apiFilters = {
        ...normalizedFilters,
        module: selectedModule?.label ?? normalizedFilters.module
      };

      const response = await ApiService.getGapsFilter(lang, apiFilters);

      const responseData = Array.isArray(response.data)
        ? response.data
        : response.data?.data || [];
      const formattedData = responseData.map(
        (item: GapFilterData, index: number) => ({
          ...item,
          key: item.gap_id || index
        })
      );
      setData(formattedData);
    } catch (error) {
      console.error('Failed to fetch Gaps Filter data:', error);
    }
  };

  useEffect(() => {
    registerReport(data, columns, 'Gaps_Filter', 'Gaps Filter');
  }, [data, columns, registerReport]);

  useEffect(() => {
    return () => {
      clearReport();
    };
  }, [clearReport]);

  useEffect(() => {
    if (!lang) return;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    handleDisplay();
  }, [lang, orgContext]);

  return (
    <Styled.VerticalSpace direction='vertical' size='large'>
      <CustomTable
        drawTable={true}
        itemsCount={data.length}
        columns={columns}
        dataSource={data}
        scroll={{ x: 1600, y: 400 }}
        loading={loading}
        rowKey={(record: GapFilterData) => record.key}
        sticky
      />
    </Styled.VerticalSpace>
  );
};

export default GapsFilter;
